/**
 * DirectiveSigningKeySection — "Directive signing key": the workspace's own
 * HMAC key for signing `directive`-tagged Team Log messages, replacing the
 * single global DIRECTIVE_SIGNING_KEY env var during the transition
 * (track:workspace-directive-keys-a, spec #48992, decision #48907).
 *
 * GETs /api/workspace/directive-signing-key (any confirmed member — Lead
 * or partner, deliberately wider than the Lead-only workspace connection
 * gate, per decision #48907's own trust-boundary paragraph). Unlike
 * MyKeySection's show-once pattern, this key stays visible on every
 * load — decision #48907 explicitly asks for that, since it's a shared
 * workspace secret a Lead may need to reference again, not a one-time
 * personal credential. Regenerate is open to every confirmed human member
 * (spec #66501 change 6, matching the server route): only member management
 * is Lead-only.
 */
import { useEffect, useState } from "react";
import { getAuthHeader } from "../../lib/auth";
import { CopyInstructionsButton } from "../CopyInstructionsButton";
import { buildDirectiveSigningKeyInstructions } from "../../lib/copyInstructions";
import { SecretField } from "./SecretField";

interface DirectiveSigningKeyResponse {
  directiveSigningKey: string;
  error?: string;
}

export function DirectiveSigningKeySection({ workspaceId = "default" }: { workspaceId?: string }) {
  const [key, setKey] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  async function load() {
    try {
      const headers = await getAuthHeader(workspaceId);
      const res = await fetch("/api/workspace/directive-signing-key", { headers });
      if (res.status === 403) {
        setForbidden(true);
        return;
      }
      const data: DirectiveSigningKeyResponse = await res.json();
      if (!res.ok) {
        setError(data.error || `HTTP ${res.status}`);
        return;
      }
      setKey(data.directiveSigningKey);
      setForbidden(false);
      setError(null);
    } catch (err) {
      console.error("[DirectiveSigningKeySection] Failed to load key:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  async function handleRegenerate() {
    setRegenerating(true);
    setError(null);
    try {
      const headers = await getAuthHeader(workspaceId);
      const res = await fetch("/api/workspace/directive-signing-key/regenerate", {
        method: "POST",
        headers,
      });
      const data: DirectiveSigningKeyResponse = await res.json();
      if (!res.ok) {
        setError(data.error || `HTTP ${res.status}`);
        return;
      }
      setKey(data.directiveSigningKey);
      setConfirming(false);
    } catch (err) {
      console.error("[DirectiveSigningKeySection] Failed to regenerate key:", err);
      setError("Network error. Please try again.");
    } finally {
      setRegenerating(false);
    }
  }

  return (
    <div id="team-section-directive-signing-key" className="tp-row">
      <div>
        <div className="tp-row-label">Directive Signing Key</div>
        <div className="tp-small">Signs every work order</div>
      </div>
      <div>
        {loading && <div className="tp-small">Loading directive signing key...</div>}

        {!loading && forbidden && (
          <div className="sc-callout info">Only a confirmed workspace member can view the directive signing key.</div>
        )}

        {!loading && !forbidden && error && <div className="sc-callout error">{error}</div>}

        {!loading && !forbidden && key && (
          <>
            <SecretField value={key} copyLabel="Copy Key" testId="signing-key-value" />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              {!confirming && (
                <button className="btn sm danger" onClick={() => setConfirming(true)} disabled={regenerating}>
                  {regenerating ? "Regenerating..." : "Regenerate"}
                </button>
              )}
              <CopyInstructionsButton text={buildDirectiveSigningKeyInstructions()} label="Copy Instructions for Your AI" />
            </div>
            {confirming && (
              <div className="sc-callout warn" style={{ marginBottom: 8 }}>
                <strong>Replace the signing key?</strong>
                <div style={{ margin: "4px 0 8px" }}>
                  Every helper in this workspace must pick up the new one. Until then, its work orders may fail to verify.
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn sm danger" onClick={handleRegenerate} disabled={regenerating}>
                    {regenerating ? "Regenerating..." : "Regenerate"}
                  </button>
                  <button className="btn sm" onClick={() => setConfirming(false)} disabled={regenerating}>Cancel</button>
                </div>
              </div>
            )}
            <div className="tp-small">
              Relay Helper and <code>relay start</code> fetch it automatically. Copy it only for <code>relay install --directive-signing-key</code>.
            </div>
            <button className="tp-btn-xs" style={{ marginTop: 6 }} onClick={() => setShowDetails((d) => !d)}>Details</button>
            {showDetails && (
              <div className="tp-small" style={{ marginTop: 6 }}>
                Shared by the whole workspace. Relay signs every work order with it, and each helper checks the signature before starting an AI partner. A Lead regenerates it if it is ever exposed, and helpers pick up the new one within 10 minutes.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
